import React, { useEffect, useState } from 'react';
import { MapPinIcon, SearchIcon } from 'lucide-react';
import { Store, MediaType, SlotStatus } from '../../data/types';
import { getStoreAvailabilitySummary } from '../../data/stores';
import { StoreRow } from './StoreRow';
import { Pagination } from '../ui/Pagination';
import { SkeletonRow } from '../ui/Skeleton';
import { Badge } from '../ui/Badge';
import { AvailabilityDot } from './AvailabilityDot';
import { EmptyState } from '../ui/EmptyState';
const allMediaTypes: {
  key: MediaType;
  label: string;
}[] = [{
  key: 'Digital Outposts',
  label: 'OUTPOSTS'
}, {
  key: 'Digital Drive Aisle',
  label: 'DRIVE AISLE'
}, {
  key: 'Digital Windows',
  label: 'WINDOWS'
}];
interface StoreTableProps {
  stores: Store[];
  onStoreClick: (store: Store) => void;
  activeMediaType?: string;
  availabilityFilter?: SlotStatus | 'all';
  selectable?: boolean;
  selectedStoreIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
}
export function StoreTable({
  stores,
  onStoreClick,
  activeMediaType = 'all',
  availabilityFilter = 'all',
  selectable = false,
  selectedStoreIds = [],
  onSelectionChange
}: StoreTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const pageSize = 15;
  // Reset to page 1 when stores/filters change
  useEffect(() => {
    setCurrentPage(1);
    setIsLoading(true);
    const timer = setTimeout(() => setIsLoading(false), 400);
    return () => clearTimeout(timer);
  }, [stores, activeMediaType, availabilityFilter]);
  // Initial load
  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);
  const visibleMediaTypes = activeMediaType === 'all' ? allMediaTypes : allMediaTypes.filter(m => m.key === activeMediaType);
  const totalPages = Math.ceil(stores.length / pageSize);
  const paginatedStores = stores.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const allSelected = paginatedStores.length > 0 && paginatedStores.every(s => selectedStoreIds.includes(s.id));
  const someSelected = selectedStoreIds.length > 0 && !allSelected;
  const handleSelectAll = () => {
    if (!onSelectionChange) return;
    if (allSelected) {
      // Deselect only the ones on the current page
      const currentPageIds = paginatedStores.map(s => s.id);
      onSelectionChange(selectedStoreIds.filter(id => !currentPageIds.includes(id)));
    } else {
      // Select all on current page, keeping existing selections
      const currentPageIds = paginatedStores.map(s => s.id);
      const newSelections = Array.from(new Set([...selectedStoreIds, ...currentPageIds]));
      onSelectionChange(newSelections);
    }
  };
  const handleToggleSelect = (storeId: string) => {
    if (!onSelectionChange) return;
    if (selectedStoreIds.includes(storeId)) {
      onSelectionChange(selectedStoreIds.filter(id => id !== storeId));
    } else {
      onSelectionChange([...selectedStoreIds, storeId]);
    }
  };
  return <div className="flex flex-col h-full" data-id="element-675">
      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto flex-1" data-id="element-676">
        <table className="w-full min-w-[700px]" data-id="element-677">
          <thead data-id="element-678">
            <tr className="border-b border-border bg-white sticky top-0 z-10" data-id="element-679">
              {selectable && <th className="px-4 py-3 text-left w-12" data-id="element-680">
                  <input type="checkbox" checked={allSelected} ref={input => {
                if (input) input.indeterminate = someSelected;
              }} onChange={handleSelectAll} className="w-4 h-4 rounded-sm border-border text-primary focus:ring-primary cursor-pointer" aria-label="Select all stores" data-id="element-681" />
                </th>}
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-682">
                Store Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-683">
                Location
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-684">
                Province
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-685">
                Tier
              </th>
              {visibleMediaTypes.map(m => <th key={m.key} className="px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide" data-id="element-686">
                  {m.label}
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-687">
            {isLoading ? Array.from({
            length: Math.min(pageSize, stores.length || 5)
          }).map((_, i) => <SkeletonRow key={i} selectable={selectable} columns={visibleMediaTypes.length} data-id="element-688" />) : paginatedStores.map((store, index) => <StoreRow key={store.id} store={store} onClick={() => onStoreClick(store)} isEven={index % 2 === 0} visibleMediaTypes={visibleMediaTypes.map(m => m.key)} availabilityFilter={availabilityFilter} selectable={selectable} isSelected={selectedStoreIds.includes(store.id)} onToggleSelect={handleToggleSelect} data-id="element-689" />)}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden flex-1 overflow-y-auto bg-surface p-4 space-y-4" data-id="element-690">
        {isLoading ? Array.from({
        length: 3
      }).map((_, i) => <div key={i} className="bg-white p-4 rounded-lg border border-border shadow-sm" data-id="element-691">
                <div className="flex justify-between mb-3" data-id="element-692">
                  <div className="space-y-2" data-id="element-693">
                    <div className="h-5 bg-surface-warm rounded w-32 animate-pulse" data-id="element-694"></div>
                    <div className="h-4 bg-surface-warm rounded w-24 animate-pulse" data-id="element-695"></div>
                  </div>
                  <div className="h-6 bg-surface-warm rounded-full w-20 animate-pulse" data-id="element-696"></div>
                </div>
                <div className="space-y-2 mt-4" data-id="element-697">
                  {visibleMediaTypes.map((m, j) => <div key={j} className="flex justify-between items-center" data-id="element-698">
                      <div className="h-4 bg-surface-warm rounded w-20 animate-pulse" data-id="element-699"></div>
                      <div className="h-4 bg-surface-warm rounded-full w-4 animate-pulse" data-id="element-700"></div>
                    </div>)}
                </div>
              </div>) : paginatedStores.map(store => {
        const summaryAvailability = getStoreAvailabilitySummary(store);
        return <div key={store.id} className={`bg-white p-4 rounded-lg border shadow-sm cursor-pointer transition-colors ${selectedStoreIds.includes(store.id) ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:border-gray-300'}`} onClick={() => {
          if (selectable) {
            handleToggleSelect(store.id);
          } else {
            onStoreClick(store);
          }
        }} data-id="element-701">
                  <div className="flex justify-between items-start mb-3" data-id="element-702">
                    <div data-id="element-703">
                      <div className="flex items-center gap-2" data-id="element-704">
                        {selectable && <input type="checkbox" checked={selectedStoreIds.includes(store.id)} onChange={() => {}} // Handled by parent div onClick
                className="w-4 h-4 rounded-sm border-border text-primary focus:ring-primary cursor-pointer" onClick={e => e.stopPropagation()} data-id="element-705" />}
                        <h3 className="font-medium text-text-primary" data-id="element-706">
                          {store.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-1.5 text-sm text-text-secondary mt-1" data-id="element-707">
                        <MapPinIcon className="w-3.5 h-3.5" data-id="element-708" />
                        {store.location}, {store.province}
                      </div>
                    </div>
                    <Badge variant={store.tier} data-id="element-709">
                      {store.tier.replace('-', ' ')}
                    </Badge>
                  </div>

                  <div className="mt-4 pt-3 border-t border-border space-y-2.5" data-id="element-710">
                    {visibleMediaTypes.map(m => {
              const status = summaryAvailability[m.key];
              return <div key={m.key} className="flex justify-between items-center text-sm" data-id="element-711">
                          <span className="text-text-secondary" data-id="element-712">{m.label}</span>
                          {status ? <div className="flex items-center gap-2" data-id="element-713">
                              <span className="text-xs text-text-tertiary capitalize" data-id="element-714">
                                {status}
                              </span>
                              <AvailabilityDot status={status} data-id="element-715" />
                            </div> : <span className="text-xs text-text-tertiary opacity-40" data-id="element-716">
                              —
                            </span>}
                        </div>;
            })}
                  </div>

                  {!selectable && <button className="w-full mt-4 py-2 text-sm font-medium text-primary bg-surface-warm rounded hover:bg-surface transition-colors" onClick={e => {
            e.stopPropagation();
            onStoreClick(store);
          }} data-id="element-717">
                      View Timeline
                    </button>}
                </div>;
      })}
      </div>

      {!isLoading && stores.length === 0 && <EmptyState icon={SearchIcon} title="No stores match your filters" description="Try adjusting your province, tier, or media type filters to see more results." className="border-none shadow-none bg-white" data-id="element-718" />}

      {stores.length > 0 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} className="sticky bottom-0 z-20" data-id="element-719" />}
    </div>;
}